/**
 * SSOT — דמי טיפול מלון: snapshot קבוע מההזמנה (לא שער תשלום עדכני).
 * Payment total: hotel_balance_usd × current_payment_rate + handling_fee_ils_snapshot
 */

import { resolveHotelPaymentSnapshot } from "./resolveHotelPaymentSnapshot.js";
import {
  isAuthoritativeHotelHandlingFeeSource,
  resolveHotelAgentHandlingFeeDisplay,
} from "./resolveHotelAgentHandlingFeeDisplay.js";

function finitePositive(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function tryParseJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    const o = JSON.parse(value);
    return o && typeof o === "object" ? o : null;
  } catch {
    return null;
  }
}

function formatUsd(n) {
  return `$${Number(n).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatIls(n) {
  return `₪${Number(n).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function roundIlsSnapshot(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return null;
  return Math.round(x);
}

function ilsLooksLikeMislabeledUsd(adminIls, adminUsd) {
  return (
    adminIls != null &&
    adminUsd != null &&
    Math.abs(adminIls - adminUsd) < 0.02
  );
}

function parseBookingCurrencySnapshot(orderRow) {
  const raw =
    orderRow?.booking_currency_snapshot ??
    orderRow?.bookingCurrencySnapshot ??
    null;
  return tryParseJson(raw);
}

function resolveBookingExchangeRate(opts) {
  const {
    bookingExchangeRate,
    adminBreakdown,
    priceCalculations,
    orderRow,
    bookingSnap,
  } = opts;

  return (
    finitePositive(bookingExchangeRate) ??
    finitePositive(adminBreakdown?.exchange_rate_at_booking) ??
    finitePositive(priceCalculations?.saved_rate) ??
    finitePositive(bookingSnap?.exchange_rate_at_booking) ??
    finitePositive(tryParseJson(orderRow?.rates)?.USD) ??
    finitePositive(tryParseJson(orderRow?.rates)?.usd) ??
    finitePositive(orderRow?.exchange_rate_at_booking) ??
    null
  );
}

function handlingFeeUsdFromBookingSnap(bookingSnap) {
  if (!bookingSnap || typeof bookingSnap !== "object") return null;
  const pcs = bookingSnap.payment_compute_snapshot;
  const breakdown =
    pcs && typeof pcs === "object" && pcs.breakdown && typeof pcs.breakdown === "object"
      ? pcs.breakdown
      : null;
  return finitePositive(breakdown?.handling_fee_usd);
}

/**
 * @param {{
 *   order?: object|null,
 *   orderReqData?: object|null,
 *   priceCalculations?: object|null,
 *   orderPayments?: Array<object>|null,
 *   bookingExchangeRate?: number|null,
 *   fallbackAgentFeeIls?: number,
 * }} opts
 */
export function resolveHotelHandlingFeeSnapshotSsot(opts = {}) {
  const orderRow = opts.orderReqData || opts.order || {};
  const priceCalculations = opts.priceCalculations || null;
  const adminBreakdown =
    (priceCalculations?.admin_price_breakdown &&
    typeof priceCalculations.admin_price_breakdown === "object"
      ? priceCalculations.admin_price_breakdown
      : null) ||
    (orderRow?.admin_price_breakdown &&
    typeof orderRow.admin_price_breakdown === "object"
      ? orderRow.admin_price_breakdown
      : null);

  const bookingSnap = parseBookingCurrencySnapshot(orderRow);
  const bookingRate = resolveBookingExchangeRate({
    bookingExchangeRate: opts.bookingExchangeRate,
    adminBreakdown,
    priceCalculations,
    orderRow,
    bookingSnap,
  });

  const adminFee = adminBreakdown?.agent_handling_fee || {};
  const adminUsd = finitePositive(adminFee.amount_usd);
  let adminIls = finitePositive(adminFee.amount_ils);
  if (ilsLooksLikeMislabeledUsd(adminIls, adminUsd)) {
    adminIls = null;
  }

  const feeDisplay = resolveHotelAgentHandlingFeeDisplay(priceCalculations, orderRow);
  if (
    isAuthoritativeHotelHandlingFeeSource(feeDisplay.sourcePath, feeDisplay.locked) &&
    (feeDisplay.amountIls != null || feeDisplay.amountUsd != null)
  ) {
    const feeUsd = feeDisplay.amountUsd;
    const feeIlsSnapshot =
      feeDisplay.amountIls != null ? roundIlsSnapshot(feeDisplay.amountIls) : null;
    const resolvedFeeIls = feeIlsSnapshot ?? 0;
    const displayPrimary =
      feeUsd != null
        ? formatUsd(feeUsd)
        : resolvedFeeIls > 0
          ? formatIls(resolvedFeeIls)
          : "—";
    const displaySecondary =
      feeUsd != null && resolvedFeeIls > 0 ? formatIls(resolvedFeeIls) : null;
    const displayFormatted =
      displaySecondary != null
        ? `${displayPrimary} / ${displaySecondary}`
        : displayPrimary;

    return {
      feeUsd,
      feeIlsSnapshot: resolvedFeeIls,
      displayPrimary,
      displaySecondary,
      displayFormatted,
      bookingExchangeRate: bookingRate,
      sourcePath: feeDisplay.sourcePath,
      hasSnapshot: resolvedFeeIls > 0 || feeUsd != null,
    };
  }

  const pcHandlingIls = finitePositive(priceCalculations?.now?.handling_fee_ils);
  const pcFeeUsd = finitePositive(priceCalculations?.fee_usd);
  const snapHandlingUsd = handlingFeeUsdFromBookingSnap(bookingSnap);

  const paymentSnap = resolveHotelPaymentSnapshot(opts.orderPayments, {
    orderId: orderRow?.id ?? orderRow?.orderid ?? null,
    logTrace: false,
  });

  let feeUsd = null;
  let feeIlsSnapshot = null;
  let sourcePath = "none";

  if (adminIls != null) {
    feeIlsSnapshot = roundIlsSnapshot(adminIls);
    feeUsd = adminUsd ?? null;
    sourcePath =
      adminFee.amount_ils_source_field ||
      adminFee.source_field ||
      "admin_price_breakdown.agent_handling_fee.amount_ils";
  } else if (pcHandlingIls != null) {
    feeIlsSnapshot = roundIlsSnapshot(pcHandlingIls);
    feeUsd = adminUsd ?? pcFeeUsd ?? snapHandlingUsd ?? null;
    sourcePath = "priceCalculations.now.handling_fee_ils";
  } else if (adminUsd != null) {
    feeUsd = adminUsd;
    feeIlsSnapshot =
      bookingRate != null
        ? roundIlsSnapshot(adminUsd * bookingRate)
        : roundIlsSnapshot(adminIls);
    sourcePath = "admin_price_breakdown.agent_handling_fee.amount_usd_x_booking_rate";
  } else if (snapHandlingUsd != null) {
    feeUsd = snapHandlingUsd;
    feeIlsSnapshot =
      bookingRate != null
        ? roundIlsSnapshot(snapHandlingUsd * bookingRate)
        : null;
    sourcePath =
      "booking_currency_snapshot.payment_compute_snapshot.breakdown.handling_fee_usd";
  } else if (paymentSnap.handlingFeeUsd != null) {
    feeUsd = paymentSnap.handlingFeeUsd;
    feeIlsSnapshot =
      paymentSnap.calculatedHandlingFeeIls != null
        ? roundIlsSnapshot(paymentSnap.calculatedHandlingFeeIls)
        : bookingRate != null
          ? roundIlsSnapshot(feeUsd * bookingRate)
          : null;
    sourcePath = "payments.snapshot.breakdown.handling_fee_usd";
  } else if (pcFeeUsd != null && bookingRate != null) {
    feeUsd = pcFeeUsd;
    feeIlsSnapshot = roundIlsSnapshot(pcFeeUsd * bookingRate);
    sourcePath = "priceCalculations.fee_usd_x_booking_rate";
  } else {
    const rawHfUsd = finitePositive(orderRow.handlingfee ?? orderRow.handling_fee);
    const feeOpt = String(orderRow.handle_fee_option || "+").trim();
    if (
      feeIlsSnapshot == null &&
      rawHfUsd != null &&
      feeOpt === "+" &&
      bookingRate != null
    ) {
      feeUsd = rawHfUsd;
      feeIlsSnapshot = roundIlsSnapshot(rawHfUsd * bookingRate);
      sourcePath = "orders.handlingfee_usd_x_booking_rate";
    }
  }

  if (feeIlsSnapshot == null) {
    const fallback = Number(opts.fallbackAgentFeeIls || 0);
    if (Number.isFinite(fallback) && fallback > 0) {
      feeIlsSnapshot = roundIlsSnapshot(fallback);
      if (sourcePath === "none") {
        sourcePath = "fallback.live_agent_fee_ils";
      }
    }
  }

  const resolvedFeeIls = feeIlsSnapshot ?? 0;
  const displayPrimary =
    feeUsd != null ? formatUsd(feeUsd) : resolvedFeeIls > 0 ? formatIls(resolvedFeeIls) : "—";
  const displaySecondary =
    feeUsd != null && resolvedFeeIls > 0 ? formatIls(resolvedFeeIls) : null;
  const displayFormatted =
    displaySecondary != null
      ? `${displayPrimary} (${displaySecondary})`
      : displayPrimary;

  return {
    feeUsd,
    feeIlsSnapshot: resolvedFeeIls,
    displayPrimary,
    displaySecondary,
    displayFormatted,
    bookingExchangeRate: bookingRate,
    sourcePath,
    hasSnapshot: resolvedFeeIls > 0 || feeUsd != null,
  };
}

/**
 * @param {number} hotelUsdBalance
 * @param {number} currentPaymentRate
 * @param {number} handlingFeeIlsSnapshot
 */
export function computeHotelPaymentTotalIls(
  hotelUsdBalance,
  currentPaymentRate,
  handlingFeeIlsSnapshot
) {
  const hotelUsd = Number(hotelUsdBalance || 0);
  const rate = Number(currentPaymentRate || 0);
  const feeIls = Number(handlingFeeIlsSnapshot || 0);
  if (hotelUsd <= 0 || rate <= 0) {
    return Number(feeIls.toFixed(2));
  }
  const hotelIls = Math.round(hotelUsd * rate * 100) / 100;
  return Number((hotelIls + feeIls).toFixed(2));
}
